import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, switchMap, tap } from 'rxjs';
import { PatientService } from './patient.service';
import { SocketService } from './socket.service';

@Injectable({
  providedIn: 'root'
})
export class PatientLabService {
  private apiUrl = `api/lab-orders`;

  private patientLabOrdersSubject = new BehaviorSubject<any[] | null>(null);
  private patientLabResultsSubject = new BehaviorSubject<any[] | null>(null);
  private editingLabOrderSubject = new BehaviorSubject<any>(null);
  /**
   * OUR OWN vendor code, off the `GET /lab-orders/patient/:id` ENVELOPE. The `vendorDoctors`
   * slot whose `code` equals this is OUR platform appearing as a party on the lab's payload and
   * must not be rendered as one. `null` until a response has been seen, and while it is null
   * nothing is suppressed — see `core/models/vendor-doctors.model.ts`.
   */
  private vendorDoctorPlatformCodeSubject = new BehaviorSubject<string | null>(null);

  patientLabOrders$ = this.patientLabOrdersSubject.asObservable();
  patientLabResults$ = this.patientLabResultsSubject.asObservable();
  editingLabOrder$ = this.editingLabOrderSubject.asObservable();
  vendorDoctorPlatformCode$ = this.vendorDoctorPlatformCodeSubject.asObservable();

  private patientId?: string;

  constructor(
    private http: HttpClient,
    private patientService: PatientService,
    private socketService: SocketService
  ) {
    this.subscribeToCurrentPatient();
    this.setupSocketListeners();
  }

  private subscribeToCurrentPatient(): void {
    this.patientService.currentPatient$.pipe(
      switchMap(patient => {
        if (patient) {
          this.patientLabOrdersSubject.next([]);
          return this.loadLabOrdersByPatient(patient.id);
        } else {
          // If there's no current patient, clear the lab orders
          this.patientLabOrdersSubject.next([]);
          return []; // Return an empty array to satisfy the Observable return type
        }
      }),
      tap((results: any) => {
        // Update the BehaviorSubject with the new or cleared lab orders
        const labOrders = results ? results.data : [];
        this.setVendorDoctorPlatformCode(results?.vendorDoctorPlatformCode);
        this.patientLabOrdersSubject.next(labOrders);
        this.updatePatientLabResults();
      })
    ).subscribe();
  }

  private setupSocketListeners(): void {
    // Current patient's ID
    let currentPatientId: string | undefined = undefined;

    // Subscribe to the current patient changes to keep track of the current patient's ID
    this.patientService.currentPatient$.subscribe(patient => {
      currentPatientId = patient ? patient.id : undefined;
      this.patientId = currentPatientId;
    });

    // Listener for when a new lab order is created
    this.socketService.listen('labOrderCreated', 'patient-lab-service').subscribe(labOrder => {
      if (labOrder.patientId === currentPatientId) {
        const currentLabOrders = this.patientLabOrdersSubject.getValue() || [];
        currentLabOrders.unshift(labOrder.labOrder);
        this.patientLabOrdersSubject.next(currentLabOrders);
        this.updatePatientLabResults();
      }
    });

    // Listener for when a lab order is updated
    this.socketService.listen('labOrderUpdated', 'patient-lab-service').subscribe(updateEvent => {
      if (updateEvent.patientId === currentPatientId) {
        const currentLabOrders = this.patientLabOrdersSubject.getValue() || [];
        const updatedLabOrder = updateEvent.labOrder || updateEvent; // Handle both structures
        const updatedLabOrders = currentLabOrders!.map(order => 
          order.id === updatedLabOrder.id ? updatedLabOrder : order
        );
        this.patientLabOrdersSubject.next(updatedLabOrders);
        this.updatePatientLabResults();
      }
    });

    // Listener for when a lab order is deleted
    this.socketService.listen('labOrderDeleted', 'patient-lab-service').subscribe(deletedLabOrder => {
      if (deletedLabOrder.patientId === currentPatientId) {
        const currentLabOrders = this.patientLabOrdersSubject.getValue() || [];
        const updatedLabOrders = currentLabOrders!.filter(order => order.id !== deletedLabOrder.orderId);
        this.patientLabOrdersSubject.next(updatedLabOrders);
        this.updatePatientLabResults();
      }
    });

    // Listener for when lab results are added
    this.socketService.listen('labResultAdded', 'patient-lab-service').subscribe(labResult => {
      if (labResult.patientId === currentPatientId) {
        const currentLabOrders = this.patientLabOrdersSubject.getValue() || [];
        const orderIndex = currentLabOrders.findIndex(order => order.id === labResult.labOrder.id);
        
        if (orderIndex >= 0) {
          // Update the lab order with the new result
          currentLabOrders[orderIndex] = labResult.labOrder;
          this.patientLabOrdersSubject.next(currentLabOrders);
          this.updatePatientLabResults();
        }
      }
    });

    // Listener for when lab results are updated
    this.socketService.listen('labResultUpdated', 'patient-lab-service').subscribe(updatedLabResult => {
      if (updatedLabResult.patientId === currentPatientId) {
        const currentLabOrders = this.patientLabOrdersSubject.getValue() || [];
        const orderIndex = currentLabOrders.findIndex(order => order.id === updatedLabResult.labOrderId);
        
        if (orderIndex >= 0) {
          // Update the lab order with the updated result
          const order = currentLabOrders[orderIndex];
          if (order.results) {
            const resultIndex = order.results.findIndex((result: any) => result.id === updatedLabResult.id);
            if (resultIndex >= 0) {
              order.results[resultIndex] = updatedLabResult;
            }
          }
          this.patientLabOrdersSubject.next(currentLabOrders);
          this.updatePatientLabResults();
        }
      }
    });

    // Listener for when lab results are deleted
    this.socketService.listen('labResultDeleted', 'patient-lab-service').subscribe(deletedLabResult => {
      if (deletedLabResult.patientId === currentPatientId) {
        const currentLabOrders = this.patientLabOrdersSubject.getValue() || [];
        const orderIndex = currentLabOrders.findIndex(order => order.id === deletedLabResult.labOrderId);
        
        if (orderIndex >= 0) {
          const order = currentLabOrders[orderIndex];
          if (order.results) {
            order.results = order.results.filter((result: any) => result.id !== deletedLabResult.id);
          }
          this.patientLabOrdersSubject.next(currentLabOrders);
          this.updatePatientLabResults();
        }
      }
    });

    // Listener for when a lab order status is updated (from worker)
    this.socketService.listen('labOrderStatusUpdated', 'patient-lab-service').subscribe(statusUpdate => {
      if (statusUpdate.patientId === currentPatientId) {
        const currentLabOrders = this.patientLabOrdersSubject.getValue() || [];
        const orderIndex = currentLabOrders.findIndex(order => order.id === statusUpdate.orderId);
        
        if (orderIndex >= 0) {
          // Update the lab order status
          currentLabOrders[orderIndex].status = statusUpdate.status;
          if (statusUpdate.provider) {
            currentLabOrders[orderIndex].labProvider = statusUpdate.provider;
          }
          this.patientLabOrdersSubject.next(currentLabOrders);
          this.updatePatientLabResults();
        }
      }
    });

    // Listener for when a lab order fails (from worker)
    this.socketService.listen('labOrderFailed', 'patient-lab-service').subscribe(failedOrder => {
      if (failedOrder.patientId === currentPatientId) {
        const currentLabOrders = this.patientLabOrdersSubject.getValue() || [];
        const orderIndex = currentLabOrders.findIndex(order => order.id === failedOrder.orderId);
        
        if (orderIndex >= 0) {
          // Update the lab order status to failed
          currentLabOrders[orderIndex].status = 'failed';
          currentLabOrders[orderIndex].errorMessage = failedOrder.error;
          this.patientLabOrdersSubject.next(currentLabOrders);
          this.updatePatientLabResults();
        }
      }
    });

    // Listener for when a lab order is completed with results
    this.socketService.listen('labOrderCompleted', 'patient-lab-service').subscribe(completedOrder => {
      if (completedOrder.patientId === currentPatientId) {
        const currentLabOrders = this.patientLabOrdersSubject.getValue() || [];
        const orderIndex = currentLabOrders.findIndex(order => order.id === completedOrder.orderId);
        
        if (orderIndex >= 0) {
          // Update the lab order status to completed with results
          currentLabOrders[orderIndex].status = 'completed';
          currentLabOrders[orderIndex].labResults = completedOrder.results;
          currentLabOrders[orderIndex].resultSummary = completedOrder.resultSummary;
          currentLabOrders[orderIndex].resultDate = new Date().toISOString();
          this.patientLabOrdersSubject.next(currentLabOrders);
          this.updatePatientLabResults();
        }
      }
    });
  }

  /**
   * Records the platform code an envelope reported.
   *
   * A response that does NOT carry one leaves the last known value alone — clobbering it with
   * null would switch suppression off mid-session and start rendering our own code as a party.
   */
  private setVendorDoctorPlatformCode(code: string | null | undefined): void {
    const next = typeof code === 'string' && code.trim() ? code.trim() : null;
    if (next === null || next === this.vendorDoctorPlatformCodeSubject.getValue()) {
      return;
    }
    this.vendorDoctorPlatformCodeSubject.next(next);
  }

  private updatePatientLabResults() {
    const labOrders = this.patientLabOrdersSubject.getValue();
    // Extract all lab results from the orders into a single array
    const resultsArray = labOrders?.reduce((acc, order) => {
      if (order.results && Array.isArray(order.results)) {
        return acc.concat(order.results);
      }
      return acc;
    }, []);

    this.patientLabResultsSubject.next([...resultsArray]);
  }

  // Lab Order Methods
  loadLabOrdersByPatient(patientId: string): Observable<any[]> {
    const token = localStorage.getItem('token');
    return this.http.get<any[]>(`${this.apiUrl}/patient/${patientId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  loadLabOrdersByGroup(groupId: string, filters?: any): Observable<any[]> {
    const token = localStorage.getItem('token');
    let url = `${this.apiUrl}/group/${groupId}`;
    
    if (filters) {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key] !== null && filters[key] !== undefined) {
          params.append(key, filters[key]);
        }
      });
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
    }
    
    return this.http.get<any[]>(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  createLabOrder(labOrderData: any): Observable<any> {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('data', JSON.stringify(labOrderData));
    
    return this.http.post<any>(this.apiUrl, formData, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  getLabOrderById(id: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.get<any>(`${this.apiUrl}/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  /**
   * A short-lived URL to the order's REQUISITION — the letterheaded, barcoded form the patient
   * takes to a collection centre. The server renders it (`services/labOrderPdfService.js`); the
   * client no longer builds a lab-order PDF of its own.
   *
   * The URL expires. Fetch it at the moment of use — do not cache it on the order object.
   *
   * @param id - The lab order id.
   * @param force - Re-render even if the server already has one stored. Use after an edit.
   */
  getRequisitionUrl(id: string, force = false): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.get<any>(`${this.apiUrl}/${id}/requisition-pdf${force ? '?force=true' : ''}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  updateLabOrderStatus(id: string, statusData: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.patch<any>(`${this.apiUrl}/${id}/status`, statusData, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  updateLabOrder(id: string, labOrderData: any): Observable<any> {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('data', JSON.stringify(labOrderData));
    
    return this.http.patch<any>(`${this.apiUrl}/${id}`, formData, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  deleteLabOrder(id: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.delete(`${this.apiUrl}/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  resubmitLabOrder(id: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/${id}/resubmit`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  // Lab Result Methods
  addLabResults(orderId: string, resultData: any): Observable<any> {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('data', JSON.stringify(resultData));
    
    return this.http.post<any>(`${this.apiUrl}/${orderId}/results`, formData, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  updateLabResult(resultId: string, resultData: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.put<any>(`${this.apiUrl}/results/${resultId}`, resultData, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  deleteLabResult(resultId: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.delete(`${this.apiUrl}/results/${resultId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  markResultsAsReviewed(orderId: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.patch<any>(`${this.apiUrl}/${orderId}/review`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  flagLabOrder(orderId: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.patch<any>(`${this.apiUrl}/${orderId}/flag`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  unflagLabOrder(orderId: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.patch<any>(`${this.apiUrl}/${orderId}/unflag`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  downloadLabResultsAsPdf(orderId: string): Observable<Blob> {
    const token = localStorage.getItem('token');
    return this.http.get(`${this.apiUrl}/${orderId}/download-pdf`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob'
    });
  }

  // Utility Methods
  setEditingLabOrder(labOrder: any | null) {
    this.editingLabOrderSubject.next(labOrder);
  }
}
